import React from 'react';
import clsx from 'clsx';
import { getCoverProcessingFeesSetting, getProcessingFeeCheckboxLabel } from '@repo/lib';

import useCheckoutState from '../../../hooks/useCheckoutState';
import useCheckoutFormHelpers from '../../../hooks/useCheckoutFormHelpers';
import useCartCost from '../../../hooks/useCartCost';
import { formatPrice } from '../../../services/formatPrice';
import Checkbox from '../../Form/components/Checkbox/Checkbox';
import StyledElement from '../../StyledElement';
import { BumpIndicator, MARK_SHAPES } from './OrderBumps';
import { getIsCoverProcessingFeeTicked } from '../../../lib/checkCoverProcessingFee';

const CHECKBOX_ID = 'cp_cover-processing-fee';

const SELECTORS = ['checkbox', 'switch', 'mark'];

const VAR_PREFIX = 'checkout-form-coverProcessingFees';
const checkboxVar = name => `var(--cp_${VAR_PREFIX}-checkbox-${name})`;

// Repoints the Checkbox's Fields input variables so it keeps its focus and checked
// rules; the forms plugin's tick image is always white, so the tick is drawn here.
const getCheckboxStyle = isSelected => ({
  width: checkboxVar('size'),
  height: checkboxVar('size'),
  backgroundImage: 'none',
  '--cp_checkout-form-fields-input-background-color': checkboxVar('background-color'),
  '--cp_checkout-form-fields-input-border-color': checkboxVar(
    isSelected ? 'border-color-selected' : 'border-color',
  ),
  '--cp_checkout-form-fields-input-border-radius': checkboxVar('border-radius'),
  '--cp_color-secondary': checkboxVar('background-color-selected'),
});

const textStyle = part => ({
  color: `var(--cp_checkout-form-coverProcessingFees-${part}-color)`,
  fontFamily: `var(--cp_checkout-form-coverProcessingFees-${part}-font-family)`,
  fontStyle: `var(--cp_checkout-form-coverProcessingFees-${part}-font-style)`,
  fontWeight: `var(--cp_checkout-form-coverProcessingFees-${part}-font-weight)`,
  fontSize: `var(--cp_checkout-form-coverProcessingFees-${part}-font-size)`,
});

export default function CoverProcessingFees() {
  const { page, form, theme, currencyLocale } = useCheckoutState();
  const { setFormStateByKey } = useCheckoutFormHelpers();
  const { processingFeeContributionOffer, currency } = useCartCost();

  if (!processingFeeContributionOffer) return null;

  const settings = getCoverProcessingFeesSetting(page);
  const isSelected = getIsCoverProcessingFeeTicked({ page, form });
  const showAmount = settings.showAmount ?? true;
  const configuredSelector = theme?.elements?.checkout?.form?.coverProcessingFees?.selector;
  const selector = SELECTORS.includes(configuredSelector) ? configuredSelector : 'checkbox';
  const configuredShape =
    theme?.elements?.checkout?.form?.coverProcessingFees?.indicator?.styles?.shape;
  const shape = MARK_SHAPES.includes(configuredShape) ? configuredShape : 'tick';
  const formatAmount = amount =>
    formatPrice({ amount, currency, locale: currencyLocale, style: 'currency' });
  // Without a contribution the shared helper returns the bare label, so the amount can sit in its own span.
  const label = getProcessingFeeCheckboxLabel(settings, null, formatAmount);

  return (
    <StyledElement
      as="label"
      htmlFor={CHECKBOX_ID}
      elementPath="checkout.form.coverProcessingFees"
      className={clsx(
        'cp_cover-processing-fees',
        { 'cp_cover-processing-fees--selected': isSelected },
        'flex items-center gap-2 cursor-pointer border-solid',
        '[border-width:var(--cp_checkout-form-coverProcessingFees-border-width,var(--cp_checkout-form-fields-input-border-width,1px))]',
        'rounded-[var(--cp_checkout-form-coverProcessingFees-border-radius,var(--cp_checkout-form-fields-input-border-radius,4px))]',
        'p-[var(--cp_checkout-form-coverProcessingFees-padding,0.75rem)]',
        // The switch and mark hide the real checkbox, so the box carries the keyboard focus ring.
        selector !== 'checkbox' &&
          'has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-[var(--cp_checkout-form-coverProcessingFees-border-color-selected,var(--cp_color-primary))]',
        isSelected
          ? [
              '[border-color:var(--cp_checkout-form-coverProcessingFees-border-color-selected,var(--cp_color-primary))]',
              'bg-[var(--cp_checkout-form-coverProcessingFees-background-color-selected,var(--cp_checkout-form-coverProcessingFees-background-color,transparent))]',
            ]
          : [
              '[border-color:var(--cp_checkout-form-coverProcessingFees-border-color,var(--cp_checkout-form-fields-input-border-color))]',
              'bg-[var(--cp_checkout-form-coverProcessingFees-background-color,transparent)]',
            ],
      )}
    >
      {selector === 'checkbox' ? (
        <span className="relative inline-flex shrink-0">
          <Checkbox
            id={CHECKBOX_ID}
            name="coverProcessingFee"
            value={isSelected}
            onChange={e => setFormStateByKey('coverProcessingFee', e.target.value)}
            className="cp_form-checkbox shrink-0"
            style={getCheckboxStyle(isSelected)}
          />
          {isSelected && (
            <svg
              viewBox="0 0 16 16"
              fill="currentColor"
              aria-hidden="true"
              className="cp_cover-processing-fees-tick pointer-events-none absolute inset-0 size-full"
              style={{ color: checkboxVar('color') }}
            >
              <path d="M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z" />
            </svg>
          )}
        </span>
      ) : (
        <BumpIndicator
          id={CHECKBOX_ID}
          isSelected={isSelected}
          onToggle={() => setFormStateByKey('coverProcessingFee', !isSelected)}
          style={selector}
          shape={shape}
          elementPath="checkout.form.coverProcessingFees"
          varPrefix={VAR_PREFIX}
        />
      )}

      <StyledElement
        as="span"
        elementPath="checkout.form.coverProcessingFees.label"
        className="cp_cover-processing-fees-label"
        style={textStyle('label')}
      >
        {label}
        {showAmount && (
          <>
            {' '}
            <StyledElement
              as="span"
              elementPath="checkout.form.coverProcessingFees.amount"
              className="cp_cover-processing-fees-amount"
              style={textStyle('amount')}
            >
              ({formatAmount(processingFeeContributionOffer.amount)})
            </StyledElement>
          </>
        )}
      </StyledElement>
    </StyledElement>
  );
}
